import Sentry from '@/services/sentry'
import OpenaiApi from '@/api/openai'
import SlideApi from '@/api/slide'
import * as spellcheckerService from '@/business-logic/spellchecker'
import store from '@/vuex/store'
import { trackSpellcheckerFetch } from '@/business-logic/ai-tracking'

const SET_MISSPELLS = 'SET_MISSPELLS'
const SET_IS_FETCHING_MISSPELLS = 'SET_IS_FETCHING_MISSPELLS'
const SET_MISSPELL_FETCH_FAILED = 'SET_MISSPELL_FETCH_FAILED'
const REMOVE_MISSPELL = 'REMOVE_MISSPELL'
const SET_IS_LOADING_DISMISS_ALL = 'SET_IS_LOADING_DISMISS_ALL'
const _state = {
  misspells: [],
  isFetchingMisspells: false,
  misspellFetchFailed: false,
  isLoadingDismissAll: false,
}

const _getters = {
  misspells(state) {
    return state.misspells
  },

  isFetchingMisspells(state) {
    return state.isFetchingMisspells
  },
  isLoadingDismissAll(state) {
    return state.isLoadingDismissAll
  },

  misspellFetchFailed(state) {
    return state.misspellFetchFailed
  },
}

const mutations = {
  [SET_MISSPELLS](state, array) {
    state.misspells = array
  },

  [SET_IS_FETCHING_MISSPELLS](state, { isFetching }) {
    state.isFetchingMisspells = isFetching
  },

  [SET_MISSPELL_FETCH_FAILED](state, { misspellFetchFailed }) {
    state.misspellFetchFailed = misspellFetchFailed
  },

  [REMOVE_MISSPELL](state, { misspellId }) {
    const misspellIndex = state.misspells.findIndex((misspell) => misspell.id === misspellId)
    if (misspellIndex < 0) return

    state.misspells.splice(misspellIndex, 1)
  },
  [SET_IS_LOADING_DISMISS_ALL](state, { isLoadingDismissAll }) {
    state.isLoadingDismissAll = isLoadingDismissAll
  },
}

const actions = {
  async fetchMisspellListAction({ commit }, presentationId) {
    try {
      commit(SET_MISSPELL_FETCH_FAILED, { misspellFetchFailed: false })
      if (!presentationId) return

      commit(SET_IS_FETCHING_MISSPELLS, { isFetching: true })
      trackSpellcheckerFetch({
        stage: 'request',
      })

      const misspells = await OpenaiApi.fetchMisspellList({ presentationId })
      const misspellCount = Array.isArray(misspells) ? misspells.length : 0

      if (!misspellCount) {
        commit(SET_IS_FETCHING_MISSPELLS, { isFetching: false })
        trackSpellcheckerFetch({
          stage: 'success',
          misspellCount,
        })
        return
      }

      const formattedMisspells = await spellcheckerService.formatMisspells({ misspells })
      commit(SET_MISSPELLS, formattedMisspells)
      commit(SET_IS_FETCHING_MISSPELLS, { isFetching: false })
      trackSpellcheckerFetch({
        stage: 'success',
        misspellCount: formattedMisspells.length,
      })
    } catch (error) {
      commit(SET_IS_FETCHING_MISSPELLS, { isFetching: false })
      commit(SET_MISSPELL_FETCH_FAILED, { misspellFetchFailed: true })
      trackSpellcheckerFetch({
        stage: 'error',
        error,
      })
      Sentry.captureException(error, {
        tags: { feature: 'spellchecker-fetch-misspell-list' },
        extra: { error },
      })
    }
  },
  async dismissAllMisspellsAction({ commit, getters, rootState }) {
    const presentationId = rootState.presentation?.id
    if (!presentationId) return

    const misspellings = getters.misspells.map((misspell) => ({
      word: misspell.misspelledWord,
      context: misspell.partialPhrase,
    }))
    commit(SET_IS_LOADING_DISMISS_ALL, { isLoadingDismissAll: true })
    await OpenaiApi.dismissBatchMisspell({ presentationId, misspellings })
    commit(SET_MISSPELLS, [])
    commit(SET_IS_FETCHING_MISSPELLS, { isFetching: false })
    commit(SET_IS_LOADING_DISMISS_ALL, { isLoadingDismissAll: false })
  },

  async dismissMisspellAction({ commit }, { presentationId, misspell }) {
    if (!presentationId || !misspell.id) return

    commit(REMOVE_MISSPELL, { misspellId: misspell.id })

    const misspellParams = {
      word: misspell.misspelledWord,
      suggestions: misspell.corrections,
      context: misspell.partialPhrase,
    }
    await OpenaiApi.dismissMisspell({ presentationId, misspell: misspellParams })
  },

  resetMisspellsAction({ commit }) {
    commit(SET_MISSPELLS, [])
  },

  async acceptMisspellAction({ commit, dispatch }, { misspell }) {
    if (!misspell || !misspell.id) return

    // Gets updated slide properties with corrected spellings using the spellcheckerService
    const {
      slideId, misspelledWord, activeCorrectionIndex, id: misspellId,
    } = misspell
    const correction = activeCorrectionIndex != null && misspell?.corrections?.[activeCorrectionIndex]
    const slide = store.getters.getSlideById({ slideId })
    const slideCopy = JSON.parse(JSON.stringify(slide))

    const correctedSlide = spellcheckerService.getCorrectedSlideProperties({
      slide: slideCopy,
      misspelledWord,
      correction,
    })

    // Front-end: Update slide content
    dispatch('updateSlideByIdAction', {
      id: slideId,
      ...correctedSlide,
    }, { root: true })
    commit(REMOVE_MISSPELL, { misspellId })
    dispatch('spellcheckerSuccessAction')

    // Back-end: Update slide content
    await SlideApi.updateSlideById(slideId, correctedSlide)
  },

  spellcheckerSuccessAction() {}, // only declare the action, plugins will listen to this to update state accordingly

  setCorrectionForMisspellAction({ commit, state }, { misspellIndex, correctionIndex }) {
    const misspells = [...state.misspells]
    const misspell = misspells?.[misspellIndex]

    if (!misspell?.id) return

    const updatedWords = misspell?.words?.map((word) => {
      if (!word?.isMisspelled) return word

      return {
        ...word,
        correction: misspell.corrections[correctionIndex],
      }
    })

    misspells[misspellIndex] = {
      ...misspell,
      activeCorrectionIndex: correctionIndex,
      words: updatedWords,
    }

    commit(SET_MISSPELLS, misspells)
  },

  showCorrectionsAction({ commit, state }, { misspelledWord, id }) {
    const updatedMisspells = state.misspells?.map((misspell) => {
      const isActiveMisspell = misspell?.misspelledWord === misspelledWord && misspell?.id === id

      return {
        ...misspell,
        isCorrectionsVisible: !!isActiveMisspell,
      }
    })

    commit(SET_MISSPELLS, updatedMisspells)
  },
}

export default {
  namespaced: true,
  state: _state,
  getters: _getters,
  actions,
  mutations,
}
